import { z } from "zod";
import {
  leaveApplicationStatusSchema,
  leaveBalanceRowSchema,
  leaveHalfDayPeriodSchema,
  leaveReadScopeSchema,
} from "@/features/leave/schemas/leave.schema";

/// The breadth surfaces: VIEW-5's balance report, VIEW-2/7's calendar,
/// VIEW-3's out-today. All three derive HOW MANY people they answer for from
/// the caller's permissions (`scope`), never from a client-supplied
/// parameter. See `leave.schema.ts` for the string-vs-number discipline this
/// file follows for every day figure.

/// A column of the report: one configured leave type. `isActive: false`
/// marks a type an admin retired; it keeps rendering for anyone still holding
/// days on it, so render it distinctly rather than omitting it outright.
/// `isLwp` exists so the client can drop the always-zero unpaid-leave column.
export const leaveTypeColumnSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    isActive: z.boolean(),
    isLwp: z.boolean(),
  })
  .strict();
export type LeaveTypeColumn = z.infer<typeof leaveTypeColumnSchema>;

/// One row of the report. `balances` is aligned 1:1 with
/// `BalanceReport.leaveTypes`, same order, zero-filled: a positional zip
/// (`rows[i].balances[j]` against `leaveTypes[j]`) is SAFE by construction on
/// the server, not by parallel sorting on the client.
export const balanceReportRowSchema = z
  .object({
    membershipId: z.string(),
    displayName: z.string(),
    departmentId: z.string().nullable(),
    balances: z.array(leaveBalanceRowSchema),
  })
  .strict();
export type BalanceReportRow = z.infer<typeof balanceReportRowSchema>;

/// Summed over the people on THIS PAGE, never the workspace. Render under a
/// heading that says so; a workspace-wide liability total is a different
/// query and does not exist yet (fix round 1 renamed this from `totals`
/// specifically to stop it being rendered as one).
export const balanceReportTotalSchema = z
  .object({
    leaveTypeId: z.string(),
    leaveTypeName: z.string(),
    credited: z.string(),
    taken: z.string(),
    available: z.string(),
    consumable: z.string(),
    pending: z.string(),
  })
  .strict();
export type BalanceReportTotal = z.infer<typeof balanceReportTotalSchema>;

export const balanceReportPageSchema = z
  .object({
    page: z.number(),
    /// What the server actually used; may be lower than what was requested
    /// (it derives the cap from the workspace's leave type count).
    pageSize: z.number(),
    /// People matching the filters, across every page.
    total: z.number(),
    hasMore: z.boolean(),
  })
  .strict();
export type BalanceReportPage = z.infer<typeof balanceReportPageSchema>;

/// GET .../leave/balances?asOf=&departmentId=&membershipId=&page=&pageSize=
export const balanceReportSchema = z
  .object({
    asOf: z.string(),
    scope: leaveReadScopeSchema,
    leaveTypes: z.array(leaveTypeColumnSchema),
    rows: z.array(balanceReportRowSchema),
    pageTotals: z.array(balanceReportTotalSchema),
    page: balanceReportPageSchema,
  })
  .strict();
export type BalanceReport = z.infer<typeof balanceReportSchema>;

/// One piece of leave on the calendar. No `reason`, ever: a team calendar
/// answers who is away and when, not why (VIEW-3's leak rule, applied here).
export const leaveCalendarItemSchema = z
  .object({
    applicationId: z.string(),
    membershipId: z.string(),
    displayName: z.string(),
    leaveTypeId: z.string(),
    leaveTypeName: z.string(),
    fromDate: z.string(),
    toDate: z.string(),
    halfDay: z.boolean(),
    halfDayDate: z.string().nullable(),
    halfDayPeriod: leaveHalfDayPeriodSchema.nullable(),
    totalLeaveDays: z.string(),
    /// Only pending or approved ever appear here; nothing else holds dates.
    status: leaveApplicationStatusSchema,
  })
  .strict();
export type LeaveCalendarItem = z.infer<typeof leaveCalendarItemSchema>;

/// GET .../leave/calendar?from=&to=&membershipId=. Span capped at 366 days,
/// items capped at 1,000, both a 400 on breach, no partial page and no
/// cursor to page around it: keep the window modest, or narrow to one person.
/// Holidays are NOT in this response; draw them from
/// `apiPaths.attendance.holidays` over the same range.
export const leaveCalendarSchema = z
  .object({
    from: z.string(),
    to: z.string(),
    scope: leaveReadScopeSchema,
    items: z.array(leaveCalendarItemSchema),
  })
  .strict();
export type LeaveCalendar = z.infer<typeof leaveCalendarSchema>;

/// GET .../leave/out-today?date=. Exactly four keys, and that is the whole
/// point: no leave type, no reason, no application id. Workspace wide,
/// approved only, held by every member regardless of their other leave
/// permissions.
export const outTodayItemSchema = z
  .object({
    membershipId: z.string(),
    displayName: z.string(),
    fromDate: z.string(),
    toDate: z.string(),
  })
  .strict();
export type OutTodayItem = z.infer<typeof outTodayItemSchema>;

export const outTodaySchema = z
  .object({
    date: z.string(),
    items: z.array(outTodayItemSchema),
  })
  .strict();
export type OutToday = z.infer<typeof outTodaySchema>;

/// APR-2's queue row. Sentence shaped rather than table shaped, and the names
/// are resolved SERVER side: a row carrying only ids would be renderable only
/// by a screen already holding the roster and the type list, which is exactly
/// what a shared inbox is not.
export const leaveToDecideItemSchema = z
  .object({
    /// The discriminator a shared leave + attendance inbox switches on. Always
    /// "leave" here; the client keys off this rather than off which fetch
    /// returned the row.
    kind: z.literal("leave"),
    applicationId: z.string(),
    membershipId: z.string(),
    /// Who the leave is FOR, not who filed it: both are allowed (APP-12).
    displayName: z.string(),
    leaveTypeId: z.string(),
    leaveTypeName: z.string(),
    fromDate: z.string(),
    toDate: z.string(),
    halfDay: z.boolean(),
    halfDayDate: z.string().nullable(),
    halfDayPeriod: leaveHalfDayPeriodSchema.nullable(),
    /// Charged days, holidays already excluded (LTY-7). A string, like every
    /// other day figure: it is the number the balance rule enforced.
    totalLeaveDays: z.string(),
    /// The applicant's own words. On this read and no other multi-person one,
    /// because only the person being asked to decide ever sees this list.
    reason: z.string(),
    status: leaveApplicationStatusSchema,
    filedAt: z.string(),
    version: z.number(),
  })
  .strict();
export type LeaveToDecideItem = z.infer<typeof leaveToDecideItemSchema>;

/// GET .../leave/applications/to-decide. `pendingCount` is counted separately
/// from `items` on purpose: the list is capped and the badge is not, so they
/// agree only while the queue is under the cap.
export const leaveToDecideSchema = z
  .object({
    scope: leaveReadScopeSchema,
    pendingCount: z.number(),
    items: z.array(leaveToDecideItemSchema),
  })
  .strict();
export type LeaveToDecide = z.infer<typeof leaveToDecideSchema>;

/// What approving did to the attendance calendar (ATT-1). `refused` is the
/// half that matters: a day already `present` keeps its verdict, because
/// evidence outranks a form filed afterwards. Reported rather than swallowed,
/// since an approval that silently failed to stamp three days is the kind of
/// thing nobody notices until payroll.
export const attendanceStampSchema = z
  .object({ written: z.array(z.string()), refused: z.array(z.string()) })
  .strict();

/// POST .../leave/applications/:id/decide. The application as it now stands,
/// with the attendance consequences attached. Null `attendance` on a rejection.
export const decidedApplicationSchema = z
  .object({
    id: z.string(),
    membershipId: z.string(),
    leaveTypeId: z.string(),
    fromDate: z.string(),
    toDate: z.string(),
    halfDay: z.boolean(),
    halfDayDate: z.string().nullable(),
    halfDayPeriod: leaveHalfDayPeriodSchema.nullable(),
    totalLeaveDays: z.string(),
    balanceAtFiling: z.string(),
    reason: z.string(),
    contactDuringLeave: z.string().nullable(),
    status: leaveApplicationStatusSchema,
    filedByUserId: z.string(),
    decidedByUserId: z.string().nullable(),
    decidedAt: z.string().nullable(),
    decisionNote: z.string().nullable(),
    departmentId: z.string().nullable(),
    version: z.number(),
    attendance: attendanceStampSchema.nullable(),
  })
  .strict();
export type DecidedApplication = z.infer<typeof decidedApplicationSchema>;

/// GET .../leave/balances/summary. The band above the table.
///
/// Every figure is "for the people you can see": `scope` echoes what the
/// server resolved from permissions, so a manager gets their whole subtree
/// (indirect reports and themselves) and an admin the workspace. Render the
/// scope beside the numbers; a liability figure with no stated breadth is a
/// figure somebody will quote at the wrong meeting.
export const leaveBalanceSummarySchema = z
  .object({
    asOf: z.string(),
    scope: leaveReadScopeSchema,
    people: z.number(),
    /// Days still held. The liability figure the page never had: `pageTotals`
    /// sums one page, which is why it is named that way.
    daysOutstanding: z.string(),
    /// Days used, netted. Same definition as the table's `taken` column: every
    /// non-grant ledger kind, so a reversal gives days back.
    daysTaken: z.string(),
    /// NET zero, not "never filed anything".
    peopleWhoTookNothing: z.number(),
    /// CARRY FORWARD only. Copy must say so: an allocation's own window
    /// closing also strands days and is not counted here.
    carryForwardLapsing: z
      .object({
        days: z.string(),
        people: z.number(),
        soonestOn: z.string().nullable(),
        through: z.string(),
      })
      .strict(),
  })
  .strict();
export type LeaveBalanceSummary = z.infer<typeof leaveBalanceSummarySchema>;
